import {
    addNavMenuItem,
    createBulkDeleteAction,
    DataService,
    DataTableConfigService,
    DeletionResponse,
    DuplicateEntityDialogComponent,
    ItemOf,
    ModalService,
    NotificationService,
    registerBulkAction,
    registerDataTableComponent,
    registerFormInputComponent
} from '@vendure/admin-ui/core';
import { inject, provideAppInitializer } from '@angular/core';
import { ProductCustomizationOptionSelectorComponent } from './components/product-customization-option-selector.component';
import { OrderedCustomizationOptionsInputComponent } from './components/ordered-customization-option-input-form.component';
import { OrderLineCustomizableAssetsComponent } from './components/orderline-customizable-assets.component';
import { CustomizationMessageFormInputComponent } from './components/custom-message-form.component';
import { PaidOptionsFormInputComponent } from './components/paid-options-form.component';
import { PaidCustomizationItemFormComponent } from './components/paid-customization-item-form.component';
import { PatternEditorInputFormComponent } from './components/pattern-editor-input-form/pattern-editor-input-form.component';
import { TextFramesFormComponent } from './components/text-frames-form.component';
import { LabeledImageFormComponent } from './components/labeled-image-form.component';

import {
    ASSIGN_PRODUCT_CUSTOMIZATION_OPTION_TO_CHANNELS,
    BULK_EXPORT_PRODUCT_CUSTOMIZATION_OPTIONS,
    DELETE_PRODUCT_CUSTOMIZATION_OPTION,
    REMOVE_PRODUCT_CUSTOMIZATION_OPTION_FROM_CHANNELS
} from './common/graphql';
import { ProductCustomizationOptionList } from '../common/ui/generated';
import { channelAction, bulkActionIsDefaultChannel } from '../common/ui/utils';
import { map, take } from 'rxjs/operators';
import { forkJoin, Observable } from 'rxjs';
import { ID } from '@vendure/core';

type GetProductCustomizationOptionListQuery = {
  productCustomizationOptions: ProductCustomizationOptionList;
};

function copyProductCustomizationOptions(context: any) {
    const modalService = context.injector.get(ModalService);
    modalService
      .fromComponent(DuplicateEntityDialogComponent<ItemOf<GetProductCustomizationOptionListQuery, 'productCustomizationOptions'>>, {
          locals: {
              entities: context.selection as any[],
              entityName: 'ProductCustomizationOption',
              title: 'Duplicate ProductCustomizationOptions',
              getEntityName: (entity: any) => entity?.name,
          },
      })
      .subscribe(async (response) => {
          if(response) {
              context.clearSelection();
              context.hostComponent.refresh();
          }
      });
}

const deleteProductCustomizationOptions = createBulkDeleteAction(
    {
        location: 'product-customization-options-list',
        requiresPermission: 'DeleteProductCustomizationOption',
        getItemName: (item:any) => item.name,
        bulkDelete: (
            dataService,
            ids: ID[],
            _retrying?: boolean,
          ): Observable<DeletionResponse[]> => {
            const deletes$ = ids.map(id =>
              dataService
                .mutate(DELETE_PRODUCT_CUSTOMIZATION_OPTION, { input: { id} })
                .pipe(map((res: any) => res.deleteProductCustomizationOption as DeletionResponse)),
            );
            return forkJoin(deletes$);
        },
    },
);

function downloadJson(data: any, filename: string) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

function bulkExportSelected(context: any) {
    const ids = context.selection.map((item: any) => item.id);
    const dataService = context.injector.get(DataService);
    const notificationService = context.injector.get(NotificationService);
    dataService.mutate(BULK_EXPORT_PRODUCT_CUSTOMIZATION_OPTIONS, { ids })
        .pipe(take(1))
        .subscribe(
            (data: any) => {
                downloadJson(data.bulkExportProductCustomizationOptions, 'product-custom-options.json');
                notificationService.success('Exported successfully');
            },
            (err: any) => notificationService.error('Failed to export: ' + err.message),
        );
}


function ensureCustomizableAssetsColumnVisible() {
    return provideAppInitializer(() => {
        const configService = inject(DataTableConfigService);
        for (const tableId of ['order-detail-list', 'return-request-details']) {
            const config = configService.getConfig(tableId);
            if (!config.visibility.includes('customizableAssets')) {
                config.visibility.push('customizableAssets');
                configService.setConfig(tableId, config);
            }
        }
    });
}

export default [
    registerFormInputComponent('product-customization-option-selector', ProductCustomizationOptionSelectorComponent),
    registerFormInputComponent('ordered-customization-options-input', OrderedCustomizationOptionsInputComponent),
    registerFormInputComponent('customization-message-form', CustomizationMessageFormInputComponent),
    registerFormInputComponent('paid-customization-item-form', PaidCustomizationItemFormComponent),
    registerFormInputComponent('paid-options-form', PaidOptionsFormInputComponent),
    registerFormInputComponent('pattern-editor-input-form', PatternEditorInputFormComponent),
    registerFormInputComponent('text-frames-form', TextFramesFormComponent),
    registerFormInputComponent('labeld-image-form', LabeledImageFormComponent),

    registerDataTableComponent({
        component: OrderLineCustomizableAssetsComponent,
        tableId: 'order-detail-list',
        columnId: 'customizableAssets',
    }),
    registerDataTableComponent({
        component: OrderLineCustomizableAssetsComponent,
        tableId: 'return-request-details',
        columnId: 'customizableAssets',
    }),
    ensureCustomizableAssetsColumnVisible(),
    addNavMenuItem(
      {
        id: 'custom-options',
        label: 'Customization Options',
        routerLink: ['/extensions/product-customization-options'],
        icon: 'settings',
      },
      'catalog'
    ),
    registerBulkAction({
        location: 'product-customization-options-list',
        label: 'Duplicate Customization Options',
        icon: 'copy',
        requiresPermission: 'CreateProductCustomizationOption',
        onClick: async (context) => {
            copyProductCustomizationOptions(context);
        },
    }),
    registerBulkAction({
        location: 'product-customization-options-list',
        label: 'Export Selected',
        icon: 'export',
        requiresPermission: 'ReadProductCustomizationOption',
        onClick: (context) => bulkExportSelected(context),
    }),
    registerBulkAction(deleteProductCustomizationOptions),
    registerBulkAction({
        isVisible: (context) => bulkActionIsDefaultChannel(context),
        location: 'product-customization-options-list',
        label: 'Delete From Channels',
        icon: 'trash',
        requiresPermission: 'UpdateProductCustomizationOption',
        onClick: async (context) =>
            channelAction(
                context,
                'remove',
                'Product Customization Options',
                REMOVE_PRODUCT_CUSTOMIZATION_OPTION_FROM_CHANNELS
            )
    }),
    registerBulkAction({
        isVisible: (context) => bulkActionIsDefaultChannel(context),
        location: 'product-customization-options-list',
        label: 'Assign To Channel',
        icon: 'plus',
        requiresPermission: 'UpdateProductCustomizationOption',
        onClick: async (context) =>
            channelAction(
                context,
                'assign',
                'Product Customization Options',
                ASSIGN_PRODUCT_CUSTOMIZATION_OPTION_TO_CHANNELS
            )
    })
];
